import React, { useMemo } from "react";
import { Switch, Input, Card } from "antd";

/**
 * Mirror of erpteson-frontend's PermissionGroupsSelector, ported to
 * Ant Design so it fits the tesoncrm tenant-creation wizard.
 *
 * Props:
 *   - permissionGroups: array<{ id, label, permissions: [{ code, label }] }>
 *   - value: Set<string> — selected permission codes
 *   - onChange(nextValue: Set<string>)
 *   - showOuterSelectAll: bool (default true)
 *   - heading: string (default "Permissions")
 */
const PermissionGroupsSelector = ({
  permissionGroups = [],
  value,
  onChange,
  showOuterSelectAll = true,
  heading = "Permissions",
}) => {
  const selected = useMemo(
    () => (value instanceof Set ? value : new Set(value || [])),
    [value]
  );

  const setMany = (codes, on) => {
    const next = new Set(selected);
    codes.forEach((c) => (on ? next.add(c) : next.delete(c)));
    onChange(next);
  };

  const setOne = (code, on) => setMany([code], on);

  const allCodes = useMemo(
    () => permissionGroups.flatMap((g) => g.permissions.map((p) => p.code)),
    [permissionGroups]
  );

  const allChecked = allCodes.length > 0 && allCodes.every((c) => selected.has(c));

  const [filter, setFilter] = React.useState("");
  const filterLower = filter.trim().toLowerCase();

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <strong>{heading}</strong>
        {showOuterSelectAll && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Switch
              size="small"
              checked={allChecked}
              onChange={(on) => setMany(allCodes, on)}
            />
            <span style={{ fontSize: 12, color: "#666" }}>Select all</span>
          </div>
        )}
      </div>

      <Input.Search
        placeholder="Filter permissions"
        allowClear
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ marginBottom: 12, maxWidth: 360 }}
      />

      <div style={{ display: "grid", gap: 12 }}>
        {permissionGroups.map((groupObj) => {
          const visiblePerms = groupObj.permissions.filter((p) => {
            if (!filterLower) return true;
            return (
              (p.label || "").toLowerCase().includes(filterLower) ||
              (p.code || "").toLowerCase().includes(filterLower)
            );
          });
          if (visiblePerms.length === 0) return null;

          const groupCodes = groupObj.permissions.map((p) => p.code);
          const allInGroup = groupCodes.length > 0 && groupCodes.every((c) => selected.has(c));

          return (
            <Card
              key={groupObj.id || groupObj.label}
              size="small"
              title={groupObj.label || "Other"}
              extra={
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Switch
                    size="small"
                    checked={allInGroup}
                    onChange={(on) => setMany(groupCodes, on)}
                  />
                  <span style={{ fontSize: 12, color: "#666" }}>Select all</span>
                </div>
              }
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {visiblePerms.map((perm) => (
                  <div
                    key={perm.code}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1px solid #f0f0f0",
                      borderRadius: 4,
                      padding: "6px 10px",
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{perm.label || perm.code}</span>
                    <Switch
                      size="small"
                      checked={selected.has(perm.code)}
                      onChange={(on) => setOne(perm.code, on)}
                    />
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionGroupsSelector;
